import type { ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Badge } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";

import { StyledSignalNameBadgeWrapper } from "./styles";

type Props = {
    isShortBadge?: boolean;
    isOnlyAdminBadge?: boolean;
    uiSignal: Readonly<UISignal<SignalFilters>>;
    children: ReactNode;
};

const SignalNameWrapperWithTags = ({ isShortBadge, isOnlyAdminBadge, uiSignal, children }: Props) => {
    const { translate } = useTranslate();

    const isAdminSignal = Boolean(uiSignal.accountSignalId);
    const showNewTag = uiSignal.isDefault && uiSignal.signalLifecycleState === "post-beta";
    const showUpdatedTag = uiSignal.isDefault && uiSignal.signalLifecycleState === "updated";
    const showBetaTag = uiSignal.signalLifecycleState === "beta";
    const hasTags = showNewTag || showUpdatedTag || showBetaTag || isAdminSignal;

    if (!hasTags) {
        return <>{children}</>;
    }

    const badge = isAdminSignal ? (
        <StyledSignalNameBadgeWrapper>
            <Badge
                type="neutral"
                data-automation="signal-admin-badge"
            >
                {translate(isShortBadge ? "si.signals.admin" : "si.signals.created-by-admin")}
            </Badge>
        </StyledSignalNameBadgeWrapper>
    ) : (showNewTag || showUpdatedTag || showBetaTag) && !isOnlyAdminBadge ? (
        <StyledSignalNameBadgeWrapper>
            {showUpdatedTag ? (
                <Badge type="new">{translate("si.common.updated")}</Badge>
            ) : (
                <Badge type={showNewTag ? "new" : "beta"} />
            )}
        </StyledSignalNameBadgeWrapper>
    ) : null;

    return (
        <Flex
            alignItems="center"
            flexGrow="1"
            flexShrink="1"
            minWidth="0"
            flexWrap="nowrap"
        >
            {children}
            {badge}
        </Flex>
    );
};

export default SignalNameWrapperWithTags;
